import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import type { ReactNode } from 'react'
import type { ConfirmationIconVariant } from './ConfirmationModalIcon'
import { ConfirmationModalIcon } from './ConfirmationModalIcon'
import {
  ConfirmationModalLayout,
  ConfirmationModalShell,
} from './ConfirmationModalShell'
import {
  confirmationBodySx,
  confirmationHeadlineSx,
  confirmationPrimaryButtonSx,
  confirmationSecondaryButtonSx,
} from './confirmationModalStyles'

export type ConfirmCancelModalIconVariant = 'cancel'

const ICON_MAP: Record<ConfirmCancelModalIconVariant, ConfirmationIconVariant> = {
  cancel: 'negative',
}

type Props = Readonly<{
  open: boolean
  iconVariant?: ConfirmCancelModalIconVariant
  headline?: string
  bodyLines?: readonly [string, string]
  message?: string
  body?: ReactNode
  confirmLabel?: string
  stayLabel?: string
  confirmDisabled?: boolean
  disableEscapeKeyDown?: boolean
  onConfirm: () => void
  onStay: () => void
  dialogTestId?: string
  stayTestId?: string
  confirmTestId?: string
}>

function resolveBodyLines(
  bodyLines: readonly [string, string] | undefined,
  message: string | undefined,
): readonly [string, string] {
  if (bodyLines) return bodyLines
  if (message) return [message, 'Your progress will be lost.']
  return ['Are you sure you want to cancel?', 'Your progress will be lost.']
}

/** Figma 4418:9942 — cancel / discard confirmation (close-circle icon). */
export function ConfirmCancelModal({
  open,
  iconVariant = 'cancel',
  headline = 'Do you want to cancel?',
  bodyLines,
  message,
  body,
  confirmLabel = 'Confirm',
  stayLabel = 'Stay',
  confirmDisabled = false,
  disableEscapeKeyDown = false,
  onConfirm,
  onStay,
  dialogTestId = 'confirm-cancel-dialog',
  stayTestId = 'confirm-cancel-stay',
  confirmTestId = 'confirm-cancel-confirm',
}: Props) {
  const [line1, line2] = resolveBodyLines(bodyLines, message)
  const headlineId = 'confirm-cancel-headline'
  const bodyId = 'confirm-cancel-body'

  return (
    <ConfirmationModalShell
      open={open}
      onClose={confirmDisabled ? undefined : onStay}
      disableEscapeKeyDown={disableEscapeKeyDown || confirmDisabled}
      dialogTestId={dialogTestId}
      ariaLabelledBy={headlineId}
      ariaDescribedBy={bodyId}
    >
      <ConfirmationModalLayout>
        <ConfirmationModalIcon
          variant={ICON_MAP[iconVariant]}
          testId={`confirm-cancel-icon-${iconVariant}`}
        />

        <Box
          id={bodyId}
          sx={{
            width: '100%',
            px: { xs: 2, sm: '24px' },
            pt: '12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <Typography id={headlineId} component="h2" sx={confirmationHeadlineSx}>
            {headline}
          </Typography>
          {body ?? (
            <Box sx={{ textAlign: 'center', maxWidth: 388, mx: 'auto' }}>
              <Typography sx={confirmationBodySx}>{line1}</Typography>
              {line2.trim() ? <Typography sx={confirmationBodySx}>{line2}</Typography> : null}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '12px',
            width: '100%',
            px: { xs: 2, sm: 3 },
            py: 3,
          }}
        >
          <Button
            variant="outlined"
            onClick={onStay}
            disabled={confirmDisabled}
            data-testid={stayTestId}
            sx={confirmationSecondaryButtonSx}
          >
            {stayLabel}
          </Button>
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={confirmDisabled}
            data-testid={confirmTestId}
            sx={confirmationPrimaryButtonSx}
          >
            {confirmLabel}
          </Button>
        </Box>
      </ConfirmationModalLayout>
    </ConfirmationModalShell>
  )
}
